import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';

import { Booking, BookingService } from 'us-web-services';
import { Button, ButtonType } from '../Button';
import { Modal } from '../Modal';

// Angular source: apps/angular/src/js/ng/booking/components/switch-payment-modal.*
// Flips a booking's payment type between credit and cash.
interface Props {
  isOpen: boolean;
  onClose: () => void;
  booking: Booking;
  onSwitched?: () => void;
}

export const SwitchPaymentModal = ({
  isOpen,
  onClose,
  booking,
  onSwitched,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const current = booking.paymentType ?? 'credit';
  const next = current === 'credit' ? 'cash' : 'credit';

  const displayName = (type: string): string =>
    type === 'credit'
      ? t('components.modals.switchPayment.paymentTypeCredit')
      : t('components.modals.switchPayment.paymentTypeCash');

  const bodyText = ((): string => {
    if (current === 'credit' && next === 'cash') return t('components.modals.switchPayment.bodyToCash');
    if (current === 'cash' && next === 'credit') return t('components.modals.switchPayment.bodyToCredit');
    return t('components.modals.switchPayment.bodyDefault');
  })();

  const submit = async (): Promise<void> => {
    if (!booking.id) return;
    setSubmitting(true);
    try {
      await BookingService.patch(
        booking.id,
        { id: booking.id, paymentType: next } as unknown as Booking,
      );
      toast.success(t('components.modals.switchPayment.success', { nextType: displayName(next) }));
      onSwitched?.();
      onClose();
    } catch {
      toast.error(t('components.modals.switchPayment.failure'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} className='w-full max-w-md'>
      <Button
        aria-label={t('common.close')}
        onClick={onClose}
        className='absolute right-2 top-2 text-gray-400 hover:text-gray-500 transition-colors'
      >
        <FontAwesomeIcon icon={faXmark} className='w-5 h-5' />
      </Button>
      <h4 className='font-semibold mb-2'>{t('components.modals.switchPayment.title')}</h4>
      <p className='mb-4'>{bodyText}</p>
      <div className='text-right'>
        <Button
          id='dismiss-switch-payment-modal'
          buttonType={ButtonType.secondary}
          className='mr-2'
          onClick={onClose}
        >
          {t('common.cancel')}
        </Button>
        <Button
          id='switch-payment'
          buttonType={ButtonType.primary}
          disabled={submitting}
          onClick={submit}
        >
          {t('components.modals.switchPayment.confirm', { nextType: displayName(next) })}
        </Button>
      </div>
    </Modal>
  );
};
